import { SortDescriptor, State } from '@progress/kendo-data-query';
import { FieldWrapper } from '@progress/kendo-react-form';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { get } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import CatalogTreeViewDropdown from 'src/components/design-system/CatalogTreeViewDropdown';
import useGuideMe from 'src/hooks/useGuideMe';
import {
  catalogTreeItemToSearchOption,
} from 'src/sections/search_products/searchProductContext.utils';
import { CatalogTreeViewItem } from 'src/types/catalog/Catalog';
import { findCatalogTreeItem } from 'src/utils/tansformCatalogsToTreeData';
import { useContextSelector } from 'use-context-selector';
import useShoppingListProductsGuideMeSteps from './GuideMeSteps';
import {
  initialListViewDataState,
  initialPictureViewDataState,
  SHOPPING_LIST_PRESERVE_SORT_FIELDS,
  ShoppingListProductsContext,
} from './ShoppingListProductsContext';
import { exportShoppingListProducts } from './utils/shoppingListExportActions';

const ShoppingListProductsFilters = () => {
  const { t } = useTranslation();

  const selectedView = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.selectedView
  );
  const setSelectedView = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.setSelectedView
  );
  const filterSearch = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.filterSearch
  );
  const handleFilterSearchChange = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleFilterSearchChange
  );
  const catalogTreeData = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.catalogTreeData
  );
  const catalogDropdownPlaceholder = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.catalogDropdownPlaceholder
  );
  // const exportShoppingListToExcel = useContextSelector(
  //   ShoppingListProductsContext,
  //   (context) => context.exportShoppingListToExcel
  // );
  // const isExportingShoppingList = useContextSelector(
  //   ShoppingListProductsContext,
  //   (context) => context.isExportingShoppingList
  // );
  const exportProducts = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.exportProducts
  );
  const shoppingList = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.shoppingList
  );
  const selectedCatalogFilter = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.selectedCatalogFilter
  );
  const setSelectedCatalogFilter = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.setSelectedCatalogFilter
  );
  const handleConfirm = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleConfirm
  );
  const setDataState = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.setDataState
  );
  const setProductSelections = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.setProductSelections
  );
  const dataState = useContextSelector(ShoppingListProductsContext, (context) => context.dataState);
  const products = useContextSelector(ShoppingListProductsContext, (context) => context.products);
  const getSelectedItems = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.getSelectedItems
  );
  const getTakeForView = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.getTakeForView
  );
  const shouldPreserveSort = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.shouldPreserveSort
  );

  const guideMeSteps = useShoppingListProductsGuideMeSteps(selectedView);
  const { guideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'shopping-list-products-guide-me',
  });

 

  const selectedCatalogValue = useMemo(
    () =>
      findCatalogTreeItem(catalogTreeData, selectedCatalogFilter.catalogId) ??
      catalogTreeData[0] ??
      null,
    [catalogTreeData, selectedCatalogFilter.catalogId]
  );

  const shouldShowUnsavedModal = useCallback(() => {
    const selectedItems = getSelectedItems();
    const isFirstPage = dataState.skip === 0 || dataState.skip === undefined;
    const isWithinRange = dataState.take === 10 || dataState.take === 25;

    if (selectedItems.length === 0) {
      return false;
    }

    const selectedProductIndexes = products.reduce((indexes: number[], product, index) => {
      if (selectedItems.some((item) => item.productKey === product.productKey)) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    const isSelectedItemsIndexWithinRange = selectedProductIndexes.every((index) => index <= 24);

    if (isFirstPage && isWithinRange) {
      return false;
    }
    if (isFirstPage && !isWithinRange && isSelectedItemsIndexWithinRange) {
      return false;
    }

    return true;
  }, [getSelectedItems, dataState, products]);

  const handleTabChange = useCallback(
    async (e: TabStripSelectEventArguments): Promise<void> => {
      const targetTake = getTakeForView(e.selected);
      const targetView = e.selected;

      const updateViewAndDataState = () => {
        setSelectedView(targetView);
        setDataState((prev: State): State => {
          const sort = get(prev, 'sort', []);
          const defaultSort =
            targetView === 0 ? initialListViewDataState.sort : initialPictureViewDataState.sort;
          const shouldPreserve = shouldPreserveSort(sort);

          return {
            ...prev,
            take: targetTake,
            skip: 0,
            sort: shouldPreserve
              ? sort.filter((descriptor: SortDescriptor) =>
                  SHOPPING_LIST_PRESERVE_SORT_FIELDS.includes(descriptor.field)
                )
              : defaultSort,
          };
        });
      };

      if (shouldShowUnsavedModal()) {
        await handleConfirm(() => {
          setProductSelections({});
          updateViewAndDataState();
        });
      } else {
        updateViewAndDataState();
      }
    },
    [
      getTakeForView,
      shouldPreserveSort,
      shouldShowUnsavedModal,
      handleConfirm,
      setProductSelections,
      setSelectedView,
      setDataState,
    ]
  );

  const handleCatalogFilterChange = useCallback(
    async (item: CatalogTreeViewItem | null) => {
      if (!item) {
        return;
      }

      await handleConfirm(() => {
        setSelectedCatalogFilter(catalogTreeItemToSearchOption(item));
        setDataState((prev) => ({ ...prev, skip: 0 }));
      });
    },
    [handleConfirm, setDataState, setSelectedCatalogFilter]
  );

  const handleExportToExcel = useCallback(async () => {
    if (!shoppingList || exportProducts.length === 0) {
      return;
    }

    try {
      await exportShoppingListProducts(shoppingList.description, exportProducts, t);
      toast.success(t('suppliersExportSuccess'));
    } catch (error) {
      console.error('[ShoppingListProductsFilters] Export failed', error);
      toast.error(t('suppliersExportError'));
    }
  }, [exportProducts, shoppingList, t]);

  return (
    <div className="mb-2 mt-3 flex w-full min-w-0 flex-wrap items-center gap-4 px-4">
      <TabStrip
        className="min-w-[190px]"
        selected={selectedView}
        onSelect={handleTabChange}
      >
        <TabStripTab contentClassName="!hidden" title={t('List View')} />
        <TabStripTab contentClassName="!hidden" title={t('Picture View')} />
      </TabStrip>

      {selectedView === 1 && (
        <div className="flex min-w-[200px] max-w-[350px] flex-[1_1_250px] items-center gap-2">
          <span className="flex shrink-0 items-center whitespace-nowrap text-xs">
            {t('Select Catalog')}
          </span>
          <div className="min-w-0 flex-1">
            <CatalogTreeViewDropdown
              data={catalogTreeData}
              onChange={handleCatalogFilterChange}
              value={selectedCatalogValue}
              placeholder={catalogDropdownPlaceholder}
              width="100%"
            />
          </div>
        </div>
      )}

      <div className="min-w-[200px] max-w-[350px] flex-[1_1_250px]">
        <FieldWrapper className="flex flex-row space-x-2">
          <Label
            editorId="shopping-list-filter-input"
            className="flex shrink-0 items-center whitespace-nowrap text-xs font-medium"
          >
            {t('Filter By')}
          </Label>
          <TextBox
            id="shopping-list-filter-input"
            max={500}
            maxLength={500}
            size="medium"
            style={{ fontSize: '14px' }}
            placeholder={t('Enter Item # or Description')}
            width="100%"
            value={filterSearch}
            onChange={async (e: TextBoxChangeEvent): Promise<void> => {
              await handleFilterSearchChange(e.value as string);
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </FieldWrapper>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        <Button size="lg" color="secondary" onClick={handleExportToExcel} disabled={!shoppingList || exportProducts.length === 0}>
          {t('exportToExcel')}
        </Button>
        {guideMe}
      </div>
    </div>
  );
};

export default ShoppingListProductsFilters;
